import { DataGrid, Dialog, TabView, TabViewPanel, TextArea } from '@myosh/odin-components';
import type { DataGridColumnSettings, DataGridSettings, JsonData, OdinDataRetrieval } from '@myosh/odin-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuditLogData, AuditLogDataItem } from '../../@types/audit';
import type { GroupAuditLogData } from '../../@types/groups';
import i18next from '../../i18n';
import { showError } from '../../services/notification.service';
import { forceAssert } from '../../utils/type-guards';
import { DataGridLoader } from '../dynamic-page/data-grid-loader.component';

interface VersionLogProps {
  auditGridData: OdinDataRetrieval;
  dataGridSettings: DataGridSettings;
  recordId?: string;
  onRowSelectionChange: (selectedRow: AuditLogData | GroupAuditLogData) => Promise<Array<AuditLogDataItem>>;
}

// the 'detail' grid which shows data about a selected row in the audit/version data grid
const columns: Array<DataGridColumnSettings> = [
  {
    id: 107,
    title: i18next.t('id'),
    field: 'id',
    visible: false,
    isIdField: true,
  },
  {
    id: 108,
    title: i18next.t('field'),
    field: 'field',
    visible: true,
    isIdField: false,
  },
  {
    id: 109,
    title: i18next.t('old'),
    field: 'oldValue',
    visible: true,
  },
  {
    id: 110,
    title: i18next.t('new'),
    field: 'newValue',
    visible: true,
  },
];

const VersionLogs = ({ auditGridData, dataGridSettings, onRowSelectionChange }: VersionLogProps) => {
  const { t } = useTranslation();

  const [detailState, setDetailState] = useState<Array<AuditLogDataItem>>([]);
  const [detailsModalData, setDetailsModalData] = useState<Array<AuditLogDataItem>>([]);
  const [rowData, setRowData] = useState<JsonData>([]);
  const [detailsModalVisible, setDetailsModalVisible] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onRowSelected = async (_gridId: string, selectedRows: JsonData) => {
    if (!selectedRows || selectedRows.length === 0) {
      setDetailState([]);
    } else {
      setIsLoading(true);
      onRowSelectionChange(forceAssert<AuditLogData | GroupAuditLogData>(selectedRows[0]))
        .then((data) => {
          setDetailState(addUniqueIds(data));
        })
        .catch(() => {
          setDetailState([]);
          showError(t('error-loading-data'));
        })
        .finally(() => setIsLoading(false));
    }
  };

  const onDetailsSelected = (_gridId: string, selectedRows: JsonData) => {
    if (!selectedRows || selectedRows.length === 0) return;
    setDetailsModalVisible(true);
    setDetailsModalData(detailState);
    setRowData(selectedRows);
  };

  //temporary callback that is neccesary until a fix is done in the odin-copmonents
  const tempCallback = useCallback(onDetailsSelected, [detailState]);

  useEffect(() => {
    if (detailsModalData && detailsModalData.length > 0 && rowData && rowData.length > 0) {
      const foundElement = detailsModalData.find((el) => el.field === rowData[0].field);

      if (foundElement) {
        const activeIndex = detailsModalData.indexOf(foundElement);
        setActiveIndex(activeIndex);
      }
    }
  }, [rowData, detailsModalData]);

  const detailGridSettings: DataGridSettings = useMemo(() => {
    return {
      columns: columns,
      autoSizeColumns: true,
      fullHeight: true,
    };
  }, []);

  return (
    <>
      <div className="mb-4 shadow-lg">
        <DataGrid
          data={auditGridData}
          gridSettings={dataGridSettings}
          onRowSelectionChange={onRowSelected}
          showSettings={false}
        />
      </div>
      <div className="shadow-lg">
        {isLoading && <DataGridLoader />}
        <DataGrid
          data={forceAssert<JsonData>(detailState)}
          gridSettings={detailGridSettings}
          showSettings={false}
          onRowSelectionChange={tempCallback}
        />
      </div>
      {detailsModalVisible && (
        <Dialog
          header={t('details')}
          visible={detailsModalVisible}
          onClose={() => setDetailsModalVisible(false)}
          containerClassName="sm:min-w-[300px] md:min-w-[500px]"
        >
          <TabView activeIndex={activeIndex}>
            {detailsModalData &&
              detailsModalData.map((el) => {
                return (
                  <TabViewPanel header={el.field} key={el.field}>
                    <TextArea name="oldValueTextArea" readOnly={true} rows={10} value={el.oldValue} label={t('old')} />
                    <TextArea name="newValueTextArea" readOnly={true} rows={10} value={el.newValue} label={t('new')} />
                  </TabViewPanel>
                );
              })}
          </TabView>
        </Dialog>
      )}
    </>
  );
};

const addUniqueIds = (data: AuditLogDataItem[]) => {
  return data.map((item) => ({
    ...item,
    id: (item.version ?? 0) + '_' + item.field,
  }));
};
export default VersionLogs;
